import {
  ConfigurationGridDataSettings,
  ConfigurationTablePageSortSettings,
  PAGE_SIZES,
} from 'src/shared/constants/table-settings-constants'
import { Dispatch, useCallback, useMemo } from 'react'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { useGridTranslateHook } from 'src/shared/utils/hooks/grid-translate-hook'
import { DataGridPro, GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { Chip, IconButton, Stack } from '@mui/material'
import { DeleteIcon, EditIcon } from 'src/shared/icons/Icons'
import { DataGridContainer } from 'src/shared/grid/DataGridContainer'
import {
  DataGridDisabledStyled,
  DataGridInactiveRowCssClassId,
} from 'src/shared/styled/data-grid/DataGridDisabledStyled'
import { ConfigurationGridToolbar } from 'src/shared/grid/ConfigurationGridToolbar'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { ROUTES } from 'src/routing/routes'
import { CONFIGURATION_STATUS } from 'src/shared/constants/constants'
import { getTranslationForStatus } from 'src/screens/administration/configuration-management/ConfigurationUtils'
import { getTranslationForFeatureProcess } from 'src/screens/administration/configuration-management/features-configuration/FeaturesConfigurationUtils'

interface FeaturesConfigGridProps {
  dataSettings: ConfigurationGridDataSettings
  tableSettings: ConfigurationTablePageSortSettings
  setTableSettings: Dispatch<ConfigurationTablePageSortSettings>
  setFeatureToBeDeleted: Dispatch<number>
}

export const FeaturesConfigGrid = ({
  dataSettings,
  tableSettings,
  setTableSettings,
  setFeatureToBeDeleted,
}: FeaturesConfigGridProps) => {
  const { rows, rowsCount, gridLoading } = dataSettings
  const { paginationModel } = tableSettings
  const { getMessage } = useMessageSource()
  const gridTranslations = useGridTranslateHook()
  const navigate = useDelayedNavigate()

  const handleRowEditClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, params: GridRenderCellParams) => {
      e.stopPropagation()

      const featureConfigId = params.row.id
      const route = ROUTES.FeaturesConfigurationIndex.nested.FeaturesConfigurationFeaturesEdit.params({
        featureConfigId,
      })
      navigate(route)
    },
    [navigate],
  )

  const handleRowDeleteClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, params: GridRenderCellParams) => {
      e.stopPropagation()

      const featureConfigId = params.row.id
      setFeatureToBeDeleted(featureConfigId)
    },
    [setFeatureToBeDeleted],
  )

  const columns = useMemo(() => {
    return [
      {
        field: 'name',
        headerName: getMessage('label.name'),
        flex: 1,
        sortable: false,
        filterable: false,
      },
      {
        field: 'feature_type',
        headerName: getMessage('label.feature.type'),
        flex: 0.5,
        sortable: false,
        filterable: false,
      },
      {
        field: 'feature_group',
        headerName: getMessage('label.feature.group'),
        flex: 0.5,
        sortable: false,
        filterable: false,
      },
      {
        field: 'allocation',
        headerName: getMessage('label.configuration.grid.allocation'),
        flex: 1,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Stack direction="row" spacing={1} height="100%" alignItems="center">
              {params.value.map((processLabel: string) => {
                const listItemKey = `${processLabel}_${params.row.id}`
                return <Chip key={listItemKey} label={getTranslationForFeatureProcess(getMessage, processLabel)} />
              })}
            </Stack>
          )
        },
      },
      {
        field: 'status',
        headerName: getMessage('label.status'),
        flex: 0.4,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => {
          const statusKey = params.value ? CONFIGURATION_STATUS.ACTIVE : CONFIGURATION_STATUS.INACTIVE
          return <Chip label={getTranslationForStatus(getMessage, statusKey)} />
        },
      },
      {
        field: 'actions',
        headerName: getMessage('label.configuration.grid.actions'),
        headerAlign: 'right',
        align: 'right',
        flex: 0.4,
        resizable: false,
        sortable: false,
        filterable: false,
        renderCell: (params) => {
          return (
            <>
              {params.row.is_deletable && (
                <span>
                  <IconButton onClick={(e) => handleRowDeleteClick(e, params)} color="primary">
                    <DeleteIcon />
                  </IconButton>
                </span>
              )}
              <span>
                <IconButton onClick={(e) => handleRowEditClick(e, params)} color="primary">
                  <EditIcon />
                </IconButton>
              </span>
            </>
          )
        },
      },
    ] as GridColDef[]
  }, [getMessage, handleRowEditClick, handleRowDeleteClick])

  const handleAddFeatureConfigClick = useCallback(() => {
    navigate(ROUTES.FeaturesConfigurationIndex.nested.FeaturesConfigurationFeaturesCreate.params({}))
  }, [navigate])

  return (
    <DataGridContainer>
      <DataGridPro
        rows={rows}
        rowCount={rowsCount}
        columns={columns}
        loading={gridLoading}
        localeText={gridTranslations}
        disableColumnPinning
        disableRowSelectionOnClick
        getRowClassName={(params) => (params.row.status ? '' : DataGridInactiveRowCssClassId)}
        disableColumnMenu
        filterMode="server"
        paginationMode="server"
        sortingMode="server"
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={(newPagination) => {
          setTableSettings({
            ...tableSettings,
            paginationModel: newPagination,
          })
        }}
        pageSizeOptions={PAGE_SIZES}
        sx={{
          boxShadow: 'none',
          border: 'none',
          ...DataGridDisabledStyled,
        }}
        slots={{
          toolbar: () => (
            <ConfigurationGridToolbar
              numResults={rowsCount}
              addButtonMessageKey="label.configuration.features.grid.toolbar.create.button"
              onAddButtonClick={handleAddFeatureConfigClick}
            />
          ),
        }}
      />
    </DataGridContainer>
  )
}
